import { useState, useEffect, useRef, useCallback } from 'react'
import { useActions, useStore, useSelector } from 'statux'
import {
    Route,
    Redirect,
    Switch,
    useLocation,
    useHistory,
    matchPath
} from 'react-router-dom'

// Utils & Custom Hooks
import { getCookie } from '../utils/cookies'
import { getCssVar } from '../utils/css'
import { scrollTo, setDocumentTitle } from '../utils/dom'
import { graphqlClient, gql } from '../utils/gql'
import { getQueryParams } from '../utils/location'
import authProvider from '../utils/auth-provider'
import useWindowSize from '../hooks/useWindowSize'
import useEventListener from '../hooks/useEventListener'

// Global Components
import Alert from '../components/Alert/Alert'
import PopupCard from '../components/PopupCard/PopupCard'
import CookieBanner from '../components/CookieBanner/CookieBanner'
import GlobalFooter from '../components/GlobalFooter/GlobalFooter'
import GlobalHeader from '../components/GlobalHeader/GlobalHeader'
import Modal from '../components/Modal/Modal'

// Top-Level Routes
import Landing from './Landing/Landing'
import Auth from './Auth/Auth'
import PrivacyTerms from './PrivacyTerms/PrivacyTerms'
import Dashboard from './Dashboard/Dashboard'

export default function Routes () {
    // Refs & DOM
    const header = useRef()

    const content = useRef()

    const contentId = 'content'

    // Routes Config via Global Store
    const routesComponents = {
        Auth,
        Landing,
        PrivacyTerms,
        Dashboard,
        Redirect
    }

    const routes = (useSelector('routes') || [])
        .map(obj => {
            const { type, ...props } = (obj || {})

            return ({
                Component: routesComponents[type] || null,
                ...props
            })
        })
        .filter(obj => obj.Component
            && (obj.path || []).length)

    const routesExact = routes
        .filter(obj => obj.exact)

    const routesDynamic = routes
        .filter(obj => !obj.exact)

    const getRoutesPathsArray = (arr) => arr
        .map(obj => obj.path)
        .reduce((acc, curr) => acc.concat(curr), [])

    const routesConfig = [{
        exact: true,
        path: getRoutesPathsArray(routesExact),
        routes: routesExact
    }, {
        exact: false,
        path: getRoutesPathsArray(routesDynamic),
        routes: routesDynamic
    }]

    const isAccessDataObj = (obj) => {
        if (!obj)
            return false

        const isMatchPath = !(obj.routes || []).length
            || obj.routes
                .some(_obj => matchPath(pathname, _obj))

        const isRoleAccess
            = !('accessControl' in obj)
                || (!obj.accessControl
                    && !user)
                || (obj.accessControl
                    && user
                    && !(obj.accessControlRoles || []).length)
                || (obj.accessControl
                    && (obj.accessControlRoles || []).length
                    && obj.accessControlRoles
                        .some(authProvider.checkRole))

        const isAdvancedMemberAccess
            = !('accessControlAdvancedMembership' in obj)
                || (obj.accessControlAdvancedMembership === false
                    && advancedMembershipAt === false)
                || (obj.accessControlAdvancedMembership
                    && advancedMembershipAt
                    && user)

        return isMatchPath
            && isRoleAccess
            && isAdvancedMemberAccess
    }

    // Global Stores, Hooks & related Functions
    const [user, setUser] = useStore('user')

    const [foundertracAccessToken, setFoundertracAccessToken]
        = useStore('foundertracAccessToken')

    const [advancedMembershipAt, setAdvancedMembershipAt]
        = useStore('advancedMembershipAt')

    /*
    const { ebpcContest, ebpcApplicationsEndDate }
        = useSelector()

    const [ebpcApplicationsOpen, setEbpcApplicationsOpen]
        = useStore('ebpcApplicationsOpen')
    */

    const globalComponents = useSelector('globalComponents') || []

    const [alert, setAlert] = useStore('alert')

    const [popupCard, setPopupCard] = useStore('popupCard')

    const cookieBanner = useSelector('cookieBanner')

    const [modal, setModal] = useStore('modal')

    const { sticky, stickyDisabled, stickyOverlay }
        = useSelector('globalHeader')

    const setStickyDisabled = useActions('globalHeader.stickyDisabled')

    const setStickyOverlay = useActions('globalHeader.stickyOverlay')

    const getStickyNavOffset = () => parseFloat(
        getCssVar('--sticky-nav-offset') || '0')

    // Location-specific
    const history = useHistory()

    const { pathname, hash } = useLocation()

    const [{
        title,
        type,
        data,
        fetchData,
        currentPathname,
        currentApiEndpoint
    }, setApiData] = useState({
        title: null,
        type: null,
        data: null,
        fetchData: null,
        currentPathname: null,
        currentApiEndpoint: null
    })

    // Set globalComponents based on access control
    useEffect(() => {
        if (!globalComponents.length)
            return

        const globalStores = {
            alert,
            setAlert,
            popupCard,
            setPopupCard
        }

        const components = globalComponents
            .filter(obj => obj.slug
                && obj.type
                && globalStores[`set${obj.type}`]
                && (!obj.modified
                    || !getCookie(`${obj.slug}-${obj.modified}`))
                && isAccessDataObj(obj))
            .filter((obj, idx, arr) => arr
                .map(_obj => _obj.slug)
                .indexOf(obj.slug) === idx)

        components
            .forEach((obj) => {
                let isChange = false

                try {
                    isChange = JSON.stringify(obj)
                        !== JSON.stringify(globalStores[obj.slug])
                }
                catch (e) {}

                if (!isChange)
                    return

                globalStores[`set${obj.type}`](obj)
            })
    }, [pathname, user, advancedMembershipAt])

    // Data helpers
    const isRenderContent = Boolean(data
        && pathname
        && (pathname === currentPathname
            || ([{
                type: 'DashboardHub',
                regex: /\/(?:search).*$/,
                replace: '$1'
            }, {
                type: 'DashboardProfile',
                regex: /\/team\/*.*$/,
                replace: ''
            }, {
                type: 'DashboardSettings',
                regex: /(\/settings)\/*.*$/,
                replace: '$1'
            }, {
                type: 'DashboardSettingsAdvancedMembers',
                regex: /(\/advanced-membership)\/*.*$/,
                replace: '$1'
            }, {
                type: 'DashboardEbpc',
                regex: /(\/page)\/*.*$/,
                replace: '$1'
            }, {
                type: 'DashboardEbpcApplication',
                regex: /(\/application)\/*.*$/,
                replace: '$1'
            }]).some(obj =>
                ((data || [])[0] || {}).type === obj.type
                    && pathname.replace(obj.regex, obj.replace)
                        === (currentPathname || '')
                            .replace(obj.regex, obj.replace))))

    const isLandingHero = isRenderContent
        && type === 'Landing'
        && (data[0] || {}).type === 'Hero'

    const isDashboard = type === 'Dashboard'
        && !authProvider.isSessionExpired()

    const isDashboardEbpcApplication = isDashboard
        && (((data || [])[0] || {}).type || '')
            === 'DashboardEbpcApplication'

    // Fetch Content Data from API Endpoint
    useEffect(() => {
        if (modal)
            setModal(false)

        const apiBasePath = '/api/rest'

        let apiEndpoint = `${apiBasePath}${
            pathname === '/'
                ? '/home'
                : pathname
                    .replace(/page\/.*$/, '')
                    .replace(/\/$/, '')}.json`

        const routeDynamic = routesDynamic
            .filter(obj => (obj.path || [])
                .some(str => pathname.match(new RegExp(`^${
                    str.replace(/\//g, '\\/')}`))))[0]

        const activeSubRoute = routeDynamic
            && (routeDynamic.routes || [])
                .filter(obj => (obj.path || [])
                    .some(str => matchPath(pathname, {
                        path: str,
                        exact: obj.exact,
                        strict: obj.strict
                    })))[0]

        // TODO: Remove type conditional once content is not FPO
        if ((activeSubRoute || {}).dataPathSuffix
                && activeSubRoute.type !== 'DashboardContent')
            apiEndpoint = `${apiBasePath}${activeSubRoute.dataPathSuffix
                .replace(/\/$/, '')}.json`

        if (apiEndpoint === currentApiEndpoint
                && ((activeSubRoute || {}).dataPathSuffix
                    || (activeSubRoute || {}).type === 'DashboardHub'))
            return setApiData(Object.assign({
                title,
                type,
                data
            }, {
                currentPathname: pathname,
                currentApiEndpoint: apiEndpoint
            }))

        window.fetch(`${apiEndpoint}${
            window.timestamp
                ? `?v=${window.timestamp}`
                : ''}`)
            .then(res => res.json())
            .then(obj => {
                obj.fetchData = obj.data || []

                obj.data = obj.fetchData
                    .filter(isAccessDataObj)

                return setApiData(Object.assign(obj, {
                    currentPathname: pathname,
                    currentApiEndpoint: apiEndpoint
                }))
            })
            .catch((err) => {
                if (routeDynamic)
                    return setApiData({
                        type: routeDynamic.Component.name,
                        data: [],
                        fetchData: [],
                        currentPathname: pathname,
                        currentApiEndpoint: apiEndpoint
                    })

                // TODO:Show error screen
                console.warn(err)
            })
    }, [pathname])

    // Update title on route changes
    useEffect(() => title
        && setDocumentTitle(title), [title])

    // Global Header stickyDisabled state
    useEffect(() => {
        if (!data)
            return

        const isDisabled = type !== 'Dashboard'
            && (window.innerWidth < 320
                || (window.innerHeight >= document.getElementById('root')
                    .clientHeight - getStickyNavOffset()))

        if ((isDisabled
                    && !stickyDisabled)
                || (!isDisabled
                    && stickyDisabled))
            setStickyDisabled(isDisabled)
    }, [data, useWindowSize()])

    // Scroll Restoration
    useEffect(() => {
        if (!data || hash)
            return

        const stickyNavOffset = !stickyDisabled
                && sticky
            ? getStickyNavOffset()
            : 0

        const scrollY = (window.history.state || {}).scrollY || 0
            + stickyNavOffset

        if (scrollY !== window.scrollY)
            window.scrollTo(0, scrollY)
    }, [data])

    // ScrollTo on Hash Changes
    useEffect(() => {
        if (!hash || !data)
            return

        // todo: use requestanimationframe instead of timeout
        if (hash.length > 1
                && !document.documentElement.classList.contains('scroll-lock'))
            setTimeout(() =>
                scrollTo(document.querySelector(hash)), 34)

        window.location.hash = ''

        window.history
            .replaceState(window.history.state, null,
                window.location.href.replace(/#.*$/, ''))
    }, [hash, data])

    // ScrollTo/Active Pathname Anchors' Click Events
    useEffect(() => {
        const hashLinkListeners = () => Array.from(document
            .querySelectorAll('a[href]:not([target=_blank])') || [])
            .forEach((el) => {
                el.onclick = (e) => {
                    const { hash } = e.target

                    if (!hash
                            || hash === '#')
                        return

                    const parsedUrl = new URL(e.target.href)

                    const targetPathname = parsedUrl.pathname || '/'

                    if (targetPathname !== pathname)
                        return

                    e.preventDefault()
                    e.stopPropagation()

                    if (modal)
                        setModal(false)

                    if (stickyOverlay)
                        setStickyOverlay(false)

                    const opts = {}

                    if (e.target.getAttribute('data-vertical-offset'))
                        opts.verticalOffset = parseFloat(
                            e.target.getAttribute('data-vertical-offset'))

                    scrollTo(
                        document.querySelector(hash)
                            || document.getElementById(contentId),
                        opts)
                }
            })

        if (isRenderContent)
            hashLinkListeners()

        const observer = 'MutationObserver' in window
                && isRenderContent
            ? new window.MutationObserver(hashLinkListeners)
            : null

        if (observer)
            observer.observe(document.body, {
                subtree: true,
                childList: true
            })

        return () => observer
            && observer.disconnect()
    }, [data, modal, stickyOverlay])

    // Reset stale accessControl data on user change
    useEffect(async () => {
        let isSetData = false

        try {
            isSetData = fetchData
                && data
                && JSON.stringify(fetchData) !== JSON.stringify(data)
        }
        catch (err) {
            console.warn(err,
                fetchData,
                data)
        }

        if (!isSetData)
            return

        const activeData = (fetchData || [])
            .filter(isAccessDataObj)

        if (JSON.stringify(activeData) === JSON.stringify(data))
            return

        setApiData({
            data: activeData,
            title,
            type,
            fetchData,
            currentPathname,
            currentApiEndpoint
        })
    }, [user])

    // Sync Advanced Membership & Foundertrac account data to store
    useEffect(async () => {
        const defaultRole = authProvider.getDefaultRole()

        let accessToken = (history.location.state || {})
            .foundertracAccessToken

        let advancedMemberAt = (history.location.state || {})
            .advancedMembershipAt

        let prevEbpcFinalist

        if (defaultRole
                && !defaultRole.match(/(admin|user)/)) {
            const ebpcQuery = defaultRole === 'member' // && ebpcContest
                ? gql`
                    ebpc_applications {
                        ebpc_contests_finalists {
                            ebpc_contest_id
                        }
                    }`
                : ''

            const profileQuery = await graphqlClient
                .request(gql`
                    query {
                        profiles (
                            limit: 1,
                            where: {
                                user: { id: { _eq: "${authProvider.getIdentity().id}" }}
                            }
                        ) {
                            advanced_membership
                            advanced_membership_at
                            foundertrac_consented_at
                            foundertrac_account {
                                access_token
                            }
                            ${ebpcQuery}
                        }
                    }
                `)
                .catch(err => {
                    console.warn(err)
                    return { profiles: [] }
                })

            const profile = ((profileQuery || {}).profiles || [])[0]

            if (((profile || {}).foundertrac_account || {}).access_token
                    && (profile || {}).foundertrac_consented_at)
                accessToken = profile.foundertrac_account.access_token

            advancedMemberAt = (profile || {}).advanced_membership_at
                || ((profile || {}).advanced_membership
                    && (profile || {}).foundertrac_consented_at)

            prevEbpcFinalist = Boolean(((profile || {}).ebpc_applications || [])
                .filter(obj => ((obj || {}).ebpc_contests_finalists || [])
                    .length)
                .length)
        }

        accessToken = accessToken || false

        advancedMemberAt = advancedMemberAt || false

        // prevEbpcFinalist = prevEbpcFinalist || false

        if (accessToken !== foundertracAccessToken) {
            setFoundertracAccessToken(accessToken)

            history.replace(
                `${history.location.pathname}${history.location.search}`,
                Object.assign(history.location.state || {}, {
                    foundertracAccessToken: accessToken
                }))
        }

        if (advancedMemberAt !== advancedMembershipAt) {
            setAdvancedMembershipAt(advancedMemberAt)

            history.replace(
                `${history.location.pathname}${history.location.search}`,
                Object.assign(history.location.state || {}, {
                    advancedMembershipAt: advancedMemberAt
                }))
        }

        console.log(prevEbpcFinalist)
        /* if (prevEbpcFinalist
                && ebpcApplicationsOpen)
            setEbpcApplicationsOpen(null)

        if (!user
                && ebpcApplicationsEndDate
                && ebpcApplicationsOpen === null)
            setEbpcApplicationsOpen((new Date()).getTime()
                <= ebpcApplicationsEndDate.getTime()) */
    }, [user])

    // Access-control via authProvider and routesConfig
    useEffect(() => {
        if (!type || !pathname)
            return

        const activeRouteType = routesConfig
            .map(obj => obj.routes
                .filter(_obj => _obj.Component.name === type)[0])
            .filter(obj => obj)[0]

        if (!activeRouteType
                || !activeRouteType.accessControlRedirect)
            return

        console.groupCollapsed('Access control')
        console.log('Type', type)
        console.log('Subtype', ((data || [])[0] || {}).type)
        console.log('Pathname', pathname)
        console.log('Current Pathname', currentPathname)
        console.log('User', user, authProvider)
        console.log('Active route type', activeRouteType)
        console.log('History.action', history.action)
        console.log('History.location', history.location)
        console.log('isRenderContent', isRenderContent)
        console.groupEnd()

        authProvider.checkAuth()
            // Route is for non-users only
            .then(() => {
                if (activeRouteType.accessControl
                        || (history.action === 'REPLACE'
                            && matchPath(pathname, {
                                path: activeRouteType.accessControlRedirect
                            })))
                    return

                history.replace(activeRouteType.accessControlRedirect)
            })
            // Route Requires Auth
            .catch(() => {
                if (!activeRouteType.accessControl
                        || (history.action === 'REPLACE'
                            && getQueryParams(history.location.search)
                                .redirect))
                    return

                const redirect = `${activeRouteType.accessControlRedirect}${
                    user === null
                        ? `${!Object.keys(getQueryParams(
                            activeRouteType.accessControlRedirect)).length
                            ? '?'
                            : '&'}redirect=${encodeURIComponent(`${
                            history.location.pathname}${
                            history.location.search}`)}`
                        : ''}`

                history
                    .replace(redirect)
            })
    }, [type, pathname, user])

    // Check auth on document.visibilityState changes
    useEventListener('visibilitychange', useCallback((e) => {
        if (document.visibilityState !== 'visible'
                || !authProvider.isSessionExpired())
            return

        authProvider.checkAuth()
            .catch(() => authProvider
                .refreshToken()
                .then(() => {
                    if (authProvider.getSession())
                        return

                    console.log('Clearing session on visibilitychange')
                    authProvider.clearSession()
                    setUser(null)
                }))
    }, []), document)

    // Cross tab session syncing
    useEventListener('storage', useCallback(async (e) => {
        if (e.key !== 'session')
            return

        const newSession = e.newValue
            ? JSON.parse(e.newValue)
            : null

        const oldSession = e.oldValue
            ? JSON.parse(e.oldValue)
            : { user: {} }

        const token = JSON.stringify(authProvider.token)

        console.groupCollapsed('Storage change')
        console.log('authProvider', authProvider)
        console.log('token', token)
        console.log('New Session', newSession)
        console.log('Old Session', oldSession)
        console.groupEnd()

        // Sync user changes
        const userObj = (newSession || {}).user || null

        if (!userObj)
            return authProvider.checkAuth()
                .catch(() => {
                    console.log('Clearing session on storage change')
                    authProvider.clearSession()
                    setUser(null)
                })

        if (userObj.fullName
                !== userObj.display_name)
            userObj.fullName = userObj.display_name

        if (userObj.avatar
                !== userObj.avatar_url)
            userObj.avatar = userObj.avatar_url

        let newUserStr

        let oldUserStr

        try {
            newUserStr = JSON.stringify(userObj)
            oldUserStr = JSON.stringify(oldSession.user)
        }
        catch (e) {
            console.warn(e)
        }

        if (newUserStr !== oldUserStr) {
            console.groupCollapsed('Storage user change')
            console.log('New Session User', userObj, newUserStr)
            console.log('Prev Session User', user, oldUserStr)
            console.groupEnd()

            const session = authProvider.getSession() || {}
            session.jwt_token = authProvider.token
            session.user = userObj

            authProvider.setSession(session)
            setUser(session.user)
        }
    }, []))

    // Styling
    let mainBgColor = null

    if (isDashboard
            || (isRenderContent
                && type === 'Landing'))
        mainBgColor = isDashboard
            ? 'var(--haze)'
            : 'var(--iron)'

    if (isDashboardEbpcApplication)
        mainBgColor = 'var(--white)'

    const userClassName = authProvider.getDefaultRole()
        ? `user user-${authProvider.getDefaultRole()}`
        : 'no-user'

    return (
        <>
            <a
                id="skip-to-content"
                href={`#${contentId}`}
                className="skip-to btn-lg"
                data-dashboard={isDashboard}
            >
                <span>Skip to content</span>
            </a>
            {data
                && isAccessDataObj(alert)
                && (
                    <Alert
                        style={{
                            backgroundColor: 'var(--paleturquoise)'
                        }}
                        {...alert}
                        className={`${userClassName} ${alert.className || ''}`
                            .trim()}
                    />)}
            {data
                && isAccessDataObj(popupCard)
                && (
                    <PopupCard
                        className={userClassName}
                        {...popupCard}
                    />)}
            {isRenderContent
                && cookieBanner
                && <CookieBanner {...cookieBanner} />}
            {data
                && (
                    <GlobalHeader
                        className={(type === 'Landing'
                                || isDashboard)
                            && 'theme-dark'}
                        headerRef={header}
                        routeType={type}
                        stickyCta={isLandingHero
                            && (data[0].cta || {})}
                        style={{
                            backgroundColor: isLandingHero
                                ? 'transparent'
                                : null
                        }}
                    />)}
            <main
                ref={content}
                id={contentId}
                className={`${userClassName} ${
                    isDashboard
                        ? 'dashboard'
                        : (type
                            ? type.toLowerCase()
                            : null)}`}
                tabIndex="-1"
                aria-label="Content"
                style={{
                    backgroundColor: mainBgColor
                }}
            >
                <Switch>
                    {routesConfig
                        .filter(obj => obj.routes.length
                            && obj.path.length)
                        .map((obj, idx) => {
                            const { routes, ...props } = obj

                            return (
                                <Route
                                    key={`route-${idx}`}
                                    {...props}
                                >
                                    {isRenderContent
                                        && (
                                            <Switch>
                                                {routes
                                                    .map((obj, _idx) => {
                                                        let {
                                                            Component,
                                                            redirect,
                                                            ...props
                                                        } = obj

                                                        redirect = redirect || {}

                                                        return (
                                                            <Route
                                                                key={`route-${
                                                                    idx}-${
                                                                    _idx}`}
                                                                {...props}
                                                            >
                                                                <Component
                                                                    data={data}
                                                                    {...redirect}
                                                                />
                                                            </Route>
                                                        )
                                                    })}
                                            </Switch>
                                        )}
                                </Route>
                            )
                        })}
                    <Route path="*">
                        <Redirect to="/" />
                    </Route>
                </Switch>
            </main>
            {data
                && !isDashboard
                && <GlobalFooter />}
            {isRenderContent
                && modal
                && <Modal {...modal} />}
        </>
    )
}